import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { useContext, useEffect } from 'react';

import { FastCreationModal, CustomAlert } from '@app/components';
import {
  ERROR,
  ADD_POSITION_LABEL,
  DATE_PICKER_FORMAT,
  OFF,
  ONE_TIME_CODE,
  GENDER,
  UNKNOWN_GENDER_ID,
  rangePickerLocale,
  ERROR_REQUIRED_FIELD,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { LeavePageContext } from '@app/providers';

import type { NUserProfile } from '@pages/Users/UserProfile/UserProfile.types';

export const MainInfo: NUserProfile.MainInfo = ({
  currentUser,
  positions,
  submitMainForm,
  setServerError,
  serverError,
  addPosition,
  isPositionsModalOpen,
  handlePositionsOk,
  handlePositionsCancel,
  modalPositionsForm,
  finishPositionsModal,
  mainForm,
  hasUserRightToCreate,
}) => {
  const { activeBranchId } = useUserPrint();

  useEffect(() => {
    const birthday = currentUser?.DateBirth
      ? currentUser?.DateBirth.slice(0, 10)
      : undefined;

    mainForm.setFieldsValue({
      NameFirst: currentUser?.NameFirst,
      NameLast: currentUser?.NameLast,
      NameMiddle: currentUser?.NameMiddle,
      Position: currentUser?.PositionId === null ? 0 : currentUser?.PositionId,
      Gender: currentUser?.Gender ?? UNKNOWN_GENDER_ID,
      Date: birthday ? dayjs(birthday) : '',
    });
  }, [currentUser, mainForm]);

  const { setInputData, setSelectorData, setDatePickerData } =
    useContext(LeavePageContext);

  return (
    <div>
      <Form
        form={mainForm}
        onFinish={(values) => submitMainForm(values, activeBranchId)}
        className="form"
        autoComplete={OFF}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text strong data-testid="surname">
              Фамилия
            </Typography.Text>

            <Form.Item name="NameLast" className="mg-0">
              <Input
                autoComplete={ONE_TIME_CODE}
                maxLength={60}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameLast',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
                data-testid="surname-input"
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label-require"
              strong
              data-testid="name"
            >
              Имя
            </Typography.Text>

            <Form.Item
              name="NameFirst"
              rules={[
                {
                  required: true,
                  message: (
                    <span data-testid="name-alert">{ERROR_REQUIRED_FIELD}</span>
                  ),
                },
              ]}
              className="mg-0"
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                maxLength={60}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameFirst',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
                data-testid="name-input"
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text strong data-testid="patronymic">
              Отчество
            </Typography.Text>

            <Form.Item name="NameMiddle" className="mg-0">
              <Input
                autoComplete={ONE_TIME_CODE}
                maxLength={80}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'NameMiddle',
                    isEditPage: true,
                    isTextField: true,
                  });
                }}
                data-testid="patronymic-input"
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text className="label" strong data-testid="position">
              Должность
            </Typography.Text>

            <Form.Item name="Position" className="mg-0">
              <Select
                size="large"
                options={positions.map((position) => {
                  return {
                    label: (
                      <span
                        data-testid={
                          position.Id === 0
                            ? 'without-position'
                            : 'position-item'
                        }
                      >
                        {position.Name}
                      </span>
                    ),
                    value: position.Id,
                  };
                })}
                popupRender={(menu) => (
                  <>
                    {hasUserRightToCreate ? (
                      <>
                        <Button
                          type="link"
                          size="small"
                          onClick={addPosition}
                          data-testid="add-position"
                        >
                          {ADD_POSITION_LABEL}
                        </Button>
                        <Divider className="position-divider" />
                        {menu}
                      </>
                    ) : (
                      <>{menu}</>
                    )}
                  </>
                )}
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'PositionId' });
                }}
                data-testid="position-select"
                classNames={{ popup: { root: 'position-drop-down' } }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text strong data-testid="gender">
              Пол
            </Typography.Text>

            <Form.Item name="Gender" className="mg-0">
              <Select
                size="large"
                options={GENDER}
                onChange={(value) => {
                  mainForm.setFieldsValue({ gender: value });
                  setSelectorData({ value, fieldName: 'Gender' });
                }}
                data-testid="gender-select"
                classNames={{ popup: { root: 'gender-drop-down' } }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text strong data-testid="birth-date">
              Дата рождения
            </Typography.Text>

            <Form.Item name="Date" className="mg-0">
              <DatePicker
                data-testid="birth-date-select"
                autoComplete={ONE_TIME_CODE}
                locale={rangePickerLocale}
                format={DATE_PICKER_FORMAT}
                size="large"
                onChange={(value) => {
                  setDatePickerData({
                    dateString: value,
                    fieldName: 'DateBirth',
                  });
                }}
                disabledDate={(current) =>
                  current.unix() >= moment().startOf('day').unix()
                }
              />
            </Form.Item>
          </div>

          {serverError && (
            <CustomAlert
              errorType={ERROR}
              serverError={serverError}
              setServerError={setServerError}
            />
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="save-button"
            data-testid="save-main-data"
          >
            Сохранить
          </Button>
        </Space>
      </Form>
      <FastCreationModal
        title="Создание должности"
        titleDataTestId="create-position-title"
        isModalOpen={isPositionsModalOpen}
        handleOk={handlePositionsOk}
        handleCancel={handlePositionsCancel}
        modalForm={modalPositionsForm}
        finishModal={finishPositionsModal}
        nameMaxLength={40}
        descriptionMaxLength={260}
      />
    </div>
  );
};
